/**
 *  Main startup hook
 */
import * as Sentry from '@sentry/nuxt'
import fr_common from '~/assets/locales/fr/common.fr.json'
import fr_validation from '~/assets/locales/fr/validation.fr.json'
import fr_hamac from '~/assets/locales/fr/hamac.fr.json'
import { createI18n } from 'vue-i18n'
import moment from 'moment/min/moment-with-locales'
import { randomInt } from 'mathjs'
import Console from '~/utils/lib/core/console'
import pubSub from '~/utils/lib/core/pub-sub'
import { broadcastChannel, EVENTS as CORE_EVENTS, STORE_PREFIX as CORE_STORE_PREFIX } from '~/utils/lib/core/types'
import { hasMessageEventData } from '~/utils/lib/core/helpers'
import { ERRORS as ACCOUNT_ERRORS, EVENTS as ACCOUNT_EVENTS, observer as accountObserver } from '~/utils/infra/account/types'
import { EVENTS as LAYOUT_EVENTS, observer as layoutObserver, STORE_PREFIX as LAYOUT_STORE_PREFIX } from '~/utils/lib/layout/types'
import storage from '~/utils/lib/core/storage'
import { STORE_PREFIX as BASE_STORE_PREFIX } from '~/utils/infra/base/types'
import { STORE_PREFIX as HAMAC_STORE_PREFIX } from '~/utils/infra/hamac/types'
import { delay, emptyCoalescing, not, toString, __, Maybe, any, noop } from '~/utils/lib/core/prelude'
import { isOnAuthenticationPages, popRefererSnapshot, routeForAnonymous, routeForUser } from '~/utils/lib/layout/redirection'
import { isPolicyInfringementError } from '~/utils/infra/common/retry-manager'
import { isGHSAPIError } from '~/utils/infra/common/types'
import { useStore_account_authentication } from '~/stores/account/authentication'
import { useStore_layout_infringement } from '~/stores/layout/infringement'
import { useStore_account_details } from '~/stores/account/details'
import { useStore_client_access } from '~/stores/client/access'
import { useStore_core_startup } from '~/stores/core/startup'
import { useStore_account_session } from '~/stores/account/session'
import { useStore_core_referer } from '~/stores/core/referer'
import { useStore_core_i18n } from '~/stores/core/i18n'
import { useStore_layout_flags } from '~/stores/layout/flags'

const DEFAULT_LANGUAGE = 'fr'

const console = new Console('plugin:main')

const i18n = createI18n(
  {
    legacy: false,
    locale: DEFAULT_LANGUAGE,
    messages: {
      fr: {
        common: fr_common,
        validation: fr_validation,
        hamac: fr_hamac,
      }
    }
  }
)

export default defineNuxtPlugin(nuxtApp => {

  const infringement = useStore_layout_infringement()
  const startup = useStore_core_startup()

  configureInternationalisation(/** @type {import('#app').NuxtApp} */ (nuxtApp))

  pubSub.subscribe(
    CORE_EVENTS.LANG_SWITCHED,
    /** @param {{ locale: string }} data */
    (data) => onLocaleSwitch(data.locale)
  )
  pubSub.subscribe(
    CORE_EVENTS.ISSUE_MONITORED,
    /** @param {{ level: string; logs: any[] }} data */
    ({ level, logs }) => manualMonitoring(level, logs)
  )

  accountObserver.on(
    ACCOUNT_EVENTS.ON_SIGNIN,
    async () => {
      infringement.$reset()
      broadcastChannel.postMessage(ACCOUNT_EVENTS.ON_SIGNIN)
      await execInitiationRequests()
      return onSignin()
    }
  )
  accountObserver.on(
    ACCOUNT_EVENTS.ON_DISPATCH_SIGNIN,
    async (/** @type {string} */ url) => {
      broadcastChannel.postMessage(ACCOUNT_EVENTS.ON_SIGNIN)
      return onDispatchSignin(url)
    }
  )
  accountObserver.on(
    ACCOUNT_EVENTS.ON_SUPER_ADMIN_SIGNIN,
    async () => {
      infringement.$reset()
      broadcastChannel.postMessage(ACCOUNT_EVENTS.ON_SIGNIN)
      await execInitiationRequests()
      return onSuperAdminSignin()
    }
  )
  accountObserver.on(
    ACCOUNT_EVENTS.ON_SIGNOUT,
    () => {
      infringement.$reset()
      broadcastChannel.postMessage(ACCOUNT_EVENTS.ON_SIGNOUT)
      return onSignout()
    }
  )
  accountObserver.on(
    ACCOUNT_EVENTS.ON_RESTORATION,
    () => execInitiationRequests()
  )
  accountObserver.on(
    ACCOUNT_EVENTS.ON_STORE_SESSION_DESTROY,
    () => dispatchStoreDestroying()
  )
  accountObserver.on(
    ACCOUNT_EVENTS.ON_REFRESH_POLICY_INFRINGEMENT,
    () => {
      broadcastChannel.postMessage(ACCOUNT_EVENTS.ON_REFRESH_POLICY_INFRINGEMENT)
      return onRefreshPolicyInfringement()
    }
  )

  layoutObserver.on(
    LAYOUT_EVENTS.ON_ANONYMOUS_USER_CATCHED,
    (/** @type {import('~/utils/lib/core/types_').Route_} */ route) => onAnonymousUserCatched(route)
  )

  broadcastChannel.addEventListener(
    'message',
    event => {
      switch(true) {

        case hasMessageEventData(event, ACCOUNT_EVENTS.ON_SIGNOUT):
          infringement.$reset()
          return onBroadcastSignout()

        case hasMessageEventData(event, ACCOUNT_EVENTS.ON_SIGNIN):
          infringement.$reset()
          return onBroadcastSignin()

        case hasMessageEventData(event, ACCOUNT_EVENTS.ON_REFRESH_POLICY_INFRINGEMENT):
          return onBroadcastRefreshPolicyInfringement()

      }
    }
  )

  return Promise.all([
      setDefaultLanguage(),
      prepareFeatureFlags(),
    ])
    .then(() => restoreAuthenticationFromCache())
    .then(() => {
      startup.$patch({ made: true })
      console.log('startup made')
    })
    // @XXX "nuxt4" silences `redirect` and `error` execution when the
    //      lifecyle has not reached the "middleware" / "page"
    //        ↳ no workaround to apply here: the sole thing we can do is a
    //          trace of the issue, which can be delegated in later stages
    //        ↳ cf. "middleware" `startup-error`
    .catch(err => {
      startup.$patch({ error: err, made: true })
      console.warn('startup catched error', err)
    })
})
/**
 * @function configureInternationalisation
 * @desc Configure "vue-i18n" library
 * @param {import('#app').NuxtApp} nuxtApp
 * @link https://vue-i18n.intlify.dev
 * @return {void}
 */
function configureInternationalisation(nuxtApp) {
  nuxtApp.vueApp.use( i18n )
}
/**
 * @function setDefaultLanguage
 * @return {Promise<void>}
 */
function setDefaultLanguage() {
  return useStore_core_i18n().setLang({ locale: DEFAULT_LANGUAGE })
}
/**
 * @function onLocaleSwitch
 * @desc
 *    - set "momentjs" locale
 *    - set "vue-i18n" locale
 * @param {string} locale
 * @return {void}
 */
function onLocaleSwitch(locale) {
  moment.locale(locale)
  // @ts-ignore: "vue-i18n" odd typing
  i18n.global.locale = locale
}
/**
 * @function prepareFeatureFlags
 * @return {Promise<void>}
 */
function prepareFeatureFlags() {
  return storage.get(LAYOUT_STORE_PREFIX, 'featureFlags')
    .then(
      value => useStore_layout_flags().setConfig(
        emptyCoalescing(value, [])
      )
    )
}
/**
 * @function dispatchStoreDestroying
 * @return {Promise<void>}
 */
function dispatchStoreDestroying() {
  return Promise.all([
      storage.destroyStore(BASE_STORE_PREFIX),
      storage.destroyStore(HAMAC_STORE_PREFIX),
      storage.destroyStore(CORE_STORE_PREFIX),
      storage.destroyStore(LAYOUT_STORE_PREFIX),
    ])
    .then(noop)
}
/**
 * @function execInitiationRequests
 * @return {Promise<void>}
 */
function execInitiationRequests() {
  const accountDetails = useStore_account_details()
  const clientAccess = useStore_client_access()

  return Promise.all([
    accountDetails.hydrate(),
    clientAccess.hydrate(),
  ])
  .then(noop)
}

/**
 * @function restoreAuthenticationFromCache
 */
function restoreAuthenticationFromCache() {
  const updateExpiration = true
  const authentication = useStore_account_authentication()

  return authentication.restore(updateExpiration)
    .then(() => accountObserver.emit(ACCOUNT_EVENTS.ON_RESTORATION))
    .catch(err => {
      switch (true) {

        // As this was an attempt of restoration, no cache retrieved is part of
        // the common process: no error here
        case
          (   err instanceof Error
          &&  toString(err) === ACCOUNT_ERRORS.NO_CACHE_RETRIEVED
          ):
            console.log('no session in cache')

            return Promise.resolve()

        // Destroy retrieved cached session if expired, as it is its design
        case
          (   err instanceof Error
          &&  toString(err) === ACCOUNT_ERRORS.CACHE_EXPIRATION_REACHED
          ):
            console.log('expired cache, will be destroyed')

            return authentication.logout()

        // Policy infringement (handled via `AccountObserver` and catched here)
        case isPolicyInfringementError(err):
            // (nothing to do, an attended redirection will be made)
            return Promise.reject(err)

        // Unknown error: destroy the whole cache (hard mode) for safety,
        // but keep the process on
        default:
            console.warn(
              `error on restoring authentication,`,
              `cache and all other storages will be destroyed`,
              err
            )

            return authentication.logout(true)
              .finally(() => Promise.reject(err))
      }
    })
}
/**
 * @function onRefreshPolicyInfringement
 * @desc
 *    - handle infringement action: as this is a peculiar event, and IDEALLY
 *      should not occur frequently, we currently deal it with parsimony
 *      (ie. not triggering any broadcast events)
 *    - two scenarios: ideally we show signin modal on the current page (to
 *      keep every informations the user had made on it, and not yet saved, due
 *      to the triggered policy infringement) ; but if not doable, we do a basic
 *      logout while keeping a referer trace
 *    - flag the captured event to prevent another computation in case of
 *      parallel requests
 * @return {Promise<void>}
 */
async function onRefreshPolicyInfringement() {
  try {
    const startup = useStore_core_startup()
    const referer = useStore_core_referer()
    const session = useStore_account_session()
    const authentication = useStore_account_authentication()
    const infringement = useStore_layout_infringement()

    const stillOnStartup = not(startup.made)
    const hasSession = session.has
    const alreadyCaptured = infringement.eventCaptured

    switch (true) {
      // @XXX "nuxt4" silences `redirect` and `error` execution when the
      //      lifecyle has not reached the "middleware" / "page"
      //        ↳ following delegation pattern started on `main` execution:
      //          check if still on startup hook, then do nothing as the
      //          "middleware" `startup-error` will take care of the business
      case stillOnStartup: {

        console.log(
          `policy infringement during startup lifecycle,`,
          `"main" execution will catch and store the error,`,
          `then will be handled into the "startup-error" middleware`
        )

        return
      }

      case alreadyCaptured: {
        console.log(
          `policy infringement already captured,`,
          `silently stop this computation to avoid repetition conflict`
        )

        return
      }

      case hasSession: {

        console.log(
          `user will keep its current session and will not be redirected,`,
          `signin modal will be shown for plain session restoration`
        )

        infringement.$patch(
          {
            displaySigninForm: true,
            eventCaptured: true
          }
        )

        return
      }

      default: {

        console.log(
          `user lacks of stored details,`,
          `session will be destroyed and will be redirected`
        )

        const currentRoute = useRouter().currentRoute.value

        referer.makeSnapshot(currentRoute)

        infringement.$patch(
          { eventCaptured: true }
        )

        await authentication.logout()

        const route = routeForAnonymous()

        await navigateTo({ path: route })

        return
      }
    }
  } catch (/** @type {any} */ err) {
    console.error(
      `fatal error while managing a user with policy infringement,`,
      `should NEVER happen as it will completely break the user experience`,
      err
    )

    showError(err)
  }
}
/**
 * @function onAnonymousUserCatched
 * @desc
 *    - HEADS UP: the whole call stack has to be synchronous or chained,
 *      otherwise it will trigger some unwanted computation
 *    - DO NOT trigger `ACCOUNT_EVENTS`: it will be prone to a broadcast
 *      concurrency retroaction
 *    - keeping a referer trace in case the user directly select a URL from
 *      zim browser history, or from a proxy redirection (see "pages/proxy.vue")
 *    - redirection has to be made by the callee (see rant comment: "nuxt4" `navigateTo` compiled code numerous issues)
 * @param {import('~/utils/lib/core/types_').Route_} route
 * @return {Promise<void>}
 */
async function onAnonymousUserCatched(route) {
  try {
    const session = useStore_account_session()
    const referer = useStore_core_referer()

    console.log(
      `anonymous will have session destroyed, redirection has to be made by the callee`
    )

    await session.destroy()

    referer.makeSnapshot(route)

  } catch (/** @type {any} */ err) {
    console.error(
      `fatal error while redirecting an anonymous user, should NEVER happen as it will completely break the user experience`,
      err
    )

    showError(err)
  }
}
/**
 * @function onSuperAdminSignin
 * @desc
 *    - redirect super admin signin to a special page
 * @return {Promise<void>}
 */
async function onSuperAdminSignin() {
  const SUPER_ADMIN_ROUTE = '/landing/super-admin-welcome'

  popRefererSnapshot() // pop the referer snapshot even though it's not used

  await navigateTo({ path: SUPER_ADMIN_ROUTE })
}
/**
 * @function onSignin
 * @desc
 *    - redirect freshly logged user to its redirection route
 * @return {Promise<void>}
 */
async function onSignin() {
  const mRefererSnapshot = popRefererSnapshot()

  // (snapshot referer redirection)
  if (Maybe.isJust(mRefererSnapshot)) {
    const route = Maybe.fromJust(mRefererSnapshot).path

    await navigateTo({ path: route })

    return
  }

  const route = await routeForUser()

  await navigateTo({ path: route })
}
/**
 * @function onSignout
 * @desc
 *    - reset sensible data from the previous session (last selected route,
 *      expanded data, etc)
 *    - redirect freshly anomymous to its redirection route
 * @return {Promise<void>}
 */
async function onSignout() {
  storage.delete(LAYOUT_STORE_PREFIX, 'selectedStructure')
  storage.delete(LAYOUT_STORE_PREFIX, 'selectedCompany')

  storage.delete(LAYOUT_STORE_PREFIX, 'expandedStructureList')

  console.log(
    `redirecting signout user`
  )

  const route = routeForAnonymous()

  await navigateTo({ path: route })
}
/**
 * @function onDispatchSignin
 * @desc
 *    - redirect freshly anonymous user to its dispatch URL
 * @param {string} url
 * @return {Promise<void>}
 */
async function onDispatchSignin(url) {
  console.log(
    `redirect signin user to dispatch URL, `,
    url
  )

  await navigateTo(url, { external: true })
}
/**
 * @function onBroadcastSignin
 * @desc
 *    - as a successful login has been made on another web browser tab, we
 *      can redirect the current one
 *    - restoring the user session
 *    - DO NOT trigger `ACCOUNT_EVENTS`: it will be prone to a broadcast
 *      concurrency retroaction
 * @return {Promise<void>}
 */
function onBroadcastSignin() {
  console.log(`broadcast signin detected`)

  const microtime = randomInt(100, 1000)

  const authentication = useStore_account_authentication()

  // (defensive: for concurrency)
  return delay(microtime)
    .then(() => authentication.restore())
    .then(() => execInitiationRequests())
    .then(async () => {
      const currentRoute = useRouter().currentRoute.value
      const isUserStillOnAuthRoute = not(isOnAuthenticationPages(currentRoute))
      const mRefererSnapshot = popRefererSnapshot()

      // (defensive: we may never know if the user has switched another page)
      if (isUserStillOnAuthRoute) {
        console.log(
          `current user not on a authentication page,`,
          `no redirection will be applied`
        )

        return
      }

      // (snapshot referer redirection)
      if (Maybe.isJust(mRefererSnapshot)) {
        const route = Maybe.fromJust(mRefererSnapshot).path

        return navigateTo({ path: route })
      }

      const route = await routeForUser()

      return navigateTo({ path: route })
    })
    .catch(err => {
      switch (true) {

        // in case when the user has made non persistent login, the restoration
        // could not be made too, silence the error and keep the process on
        case
          (   err instanceof Error
          &&  toString(err) === ACCOUNT_ERRORS.CACHE_EXPIRATION_REACHED
          ):
          console.log(
            'expired session, seems like an non persistent login',
            'nothing more to do ignore this instance of the app'
          )

          return Promise.resolve()

        // decision to safely destroy the session here, as it is the more
        // logical thing
        default:

          console.warn(
            `error during broadcast signin execution`,
            `will call the signout process to be executed`
          )

          accountObserver.emit(ACCOUNT_EVENTS.ON_SIGNOUT)

          return Promise.resolve(err)
      }
    })
}
/**
 * @function onBroadcastSignout
 * @desc
 *    - consider current user now as an anonymous
 *    - retain current route as a snapshot (while authenticated)
 *    - redirecting to an anonymous friendly route
 * @return {Promise<void>}
 */
async function onBroadcastSignout() {
  try {
    const session = useStore_account_session()
    const referer = useStore_core_referer()

    console.log(`broadcast signout detected`)

    const hasSession = session.has
    const currentRoute = useRouter().currentRoute.value

    if (hasSession)
      referer.makeSnapshot(currentRoute)

    const microtime = randomInt(100, 1000)

    // (defensive: for concurrency)
    await delay(microtime)

    // just remove the session account dynamic cache, no need to destroy it
    // concretely as we do not know what other web browser tabs are doing
    const dynamicCacheReset = {
      refreshToken: '',
      accessToken: ''
    }

    session.$patch(dynamicCacheReset)

    const route = routeForAnonymous()

    await navigateTo({ path: route })

  } catch (/** @type {any} */ err) {
    console.error(
      `fatal error while handling a broadcast signout event,`,
      `should NEVER happen as it will completely break the user experience`,
      err
    )

    await showError(err)
  }
}
/**
 * @function onBroadcastRefreshPolicyInfringement
 * @desc
 *    - identical process as the `onRefreshPolicyInfringement()`
 *    - as of this, the executed scenario (either a logout due to lack of
 *      session data, or a floated login modal displayed) made on the source
 *      thread triggering the event OUGHT to be same one here
 * @return {Promise<void>}
 */
function onBroadcastRefreshPolicyInfringement() {
  console.log(
    `catched a broadcast refresh policy infringement`
  )

  return onRefreshPolicyInfringement()
}
/**
 * @function manualMonitoring
 * @desc
 *    - manual Sentry sending
 *    - client logic regarding criticity
 *      - warn: logs containing server error will not be send
 *      - error: every log will be send
 * @param {"warn"|"error"|string} level
 * @param {Array<any>} logs
 */
function manualMonitoring(level, logs) {
  switch (level) {

    case 'warn': {
      const containMicroServiceError = any(
        isGHSAPIError,
        logs
      )

      if (containMicroServiceError)
        break

      Sentry.captureException(logs)
      break
    }

    case 'error':
      Sentry.captureException(logs)
      break
  }
}
